var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { styled, useTheme } from '@mui/material';
import { times } from 'lodash';
import { Fragment, useMemo } from 'react';
import { PlanType } from '../../enum';
import { useControlledExpansion, useTableRowExpansion } from '../annual/common/selectors';
import { useTerminologyTranslation } from '../app/terminology';
import { Table, TableBody, TableCell, TableContainer, TableRow } from '../common';
import { ExpandableRowLabel, TABLE_HIERARCHY_INDENTS } from '../common/tableHierarchy';
import { usePeriodReportChunkSize } from '../period/selectors';
import { useReportAPI } from '../report/selectors';
import { SUMMARY_TRAINING_KEY, SUMMARY_TRAINING_KEY_BOTTOM } from './constants';
import { useScenarioSelected, useTrainingSummaryTable } from './selectors';
import { TRAINING_STICKIES, TrainingTableHead } from './trainingTable';
var StyledTableContainer = styled(TableContainer)(function () {
  return {
    maxHeight: '75vh',
    position: 'relative',
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: 0,
      zIndex: 10
    }
  };
});
var getLobExpansionKey = function (lobName) {
  return "lob:".concat(lobName);
};
var getQueueExpansionKey = function (lobName, queue) {
  return "".concat(getLobExpansionKey(lobName), ":queue:").concat(queue.id);
};
var getLocationExpansionKey = function (lobName, queue, location) {
  return "".concat(getQueueExpansionKey(lobName, queue), ":location:").concat(location.id);
};
var getOverallLocationExpansionKey = function (location) {
  return "overall:location:".concat(location.id);
};
var getQueueDescendantExpansionKeys = function (lobName, queue) {
  var _a, _b;
  return (_b = (_a = queue.queueLocations) === null || _a === void 0 ? void 0 : _a.map(function (loc) {
    return getLocationExpansionKey(lobName, queue, loc);
  })) !== null && _b !== void 0 ? _b : [];
};
var getLobDescendantExpansionKeys = function (lob) {
  return lob.queues.flatMap(function (queue) {
    return __spreadArray([getQueueExpansionKey(lob.name, queue)], getQueueDescendantExpansionKeys(lob.name, queue), true);
  });
};
var SummaryTrainingRows = function (_a) {
  var _b;
  var id = _a.id,
    trainings = _a.trainings,
    bgColor = _a.bgColor,
    _c = _a.isSummary,
    isSummary = _c === void 0 ? false : _c,
    nestLevel = _a.nestLevel;
  var t = useTerminologyTranslation('report', {
    keyPrefix: 'training.summary'
  }).t;
  var rowKeys = isSummary ? SUMMARY_TRAINING_KEY_BOTTOM : SUMMARY_TRAINING_KEY;
  var theme = useTheme();
  var reportData = useReportAPI()[0];
  var isMonthly = ((_b = reportData.data) === null || _b === void 0 ? void 0 : _b.planType) === PlanType.Monthly;
  var paddingLeft = nestLevel ? TABLE_HIERARCHY_INDENTS[nestLevel] : undefined;
  return _jsx(_Fragment, {
    children: rowKeys.map(function (key) {
      return _jsxs(TableRow, {
        children: [_jsx(TableCell, {
          stickySizes: TRAINING_STICKIES,
          color: theme.palette.background.default,
          component: isSummary ? 'th' : 'td',
          align: "left",
          value: t(key),
          bold: true,
          style: {
            paddingLeft: paddingLeft
          }
        }), trainings.map(function (training, index) {
          var isTotalColumn = !((index + 1) % 13);
          return _jsx(TableCell, {
            greyColor: isMonthly ? isTotalColumn : index === trainings.length - 1,
            color: bgColor,
            bold: isSummary,
            highlightBorder: isTotalColumn && isMonthly && trainings.length - 1 !== index,
            value: training[key]
          }, "".concat(index, ".").concat(id, ".").concat(key));
        })]
      }, key + id);
    })
  });
};
var HierarchyHeaderRow = function (_a) {
  var _b;
  var displayName = _a.displayName,
    backgroundColor = _a.backgroundColor,
    textColor = _a.textColor,
    expanded = _a.expanded,
    onToggle = _a.onToggle,
    nestLevel = _a.nestLevel;
  var paddingLeft = nestLevel ? TABLE_HIERARCHY_INDENTS[nestLevel] : undefined;
  var reportData = useReportAPI()[0].data;
  var chunkSize = usePeriodReportChunkSize();
  var totalYears = ((_b = reportData === null || reportData === void 0 ? void 0 : reportData.periodLength) !== null && _b !== void 0 ? _b : 0) / chunkSize;
  return _jsxs(TableRow, {
    children: [_jsx(TableCell, __assign({
      stickySizes: TRAINING_STICKIES,
      color: backgroundColor,
      align: "left",
      bold: true,
      sx: {
        color: textColor
      },
      style: {
        paddingLeft: paddingLeft
      }
    }, {
      children: _jsx(ExpandableRowLabel, __assign({
        expanded: expanded,
        onToggle: onToggle
      }, {
        children: displayName
      }))
    })), times(totalYears, function (year) {
      return _jsxs(Fragment, {
        children: [times(chunkSize, function (i) {
          return _jsx(TableCell, {
            color: backgroundColor
          }, "".concat(year, "-").concat(i));
        }), _jsx(TableCell, {
          color: backgroundColor,
          highlightBorder: year < totalYears - 1
        })]
      }, "".concat(year));
    })]
  });
};
var LocationRowComponent = function (_a) {
  var location = _a.location,
    bgColor = _a.bgColor,
    expansionKey = _a.expansionKey,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var _b = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey);
    } : undefined, true),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  var theme = useTheme();
  return _jsxs(_Fragment, {
    children: [_jsx(HierarchyHeaderRow, {
      displayName: location.name,
      backgroundColor: bgColor,
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: 3
    }), expanded && _jsx(SummaryTrainingRows, {
      id: location.id,
      trainings: location.trainings,
      bgColor: theme.palette.common.white,
      nestLevel: 4
    })]
  });
};
var QueueRowComponent = function (_a) {
  var _b;
  var queue = _a.queue,
    lobName = _a.lobName,
    locationBgColor = _a.locationBgColor,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var expansionKey = getQueueExpansionKey(lobName, queue);
  var descendantKeys = getQueueDescendantExpansionKeys(lobName, queue);
  var _c = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey, descendantKeys);
    } : undefined, true),
    expanded = _c.expanded,
    handleToggle = _c.handleToggle;
  var theme = useTheme();
  return _jsxs(_Fragment, {
    children: [_jsx(HierarchyHeaderRow, {
      displayName: queue.name,
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: 2,
      backgroundColor: theme.palette.common.white
    }), expanded && _jsx(_Fragment, {
      children: ((_b = queue.queueLocations) === null || _b === void 0 ? void 0 : _b.length) ? queue.queueLocations.map(function (loc) {
        return _jsx(LocationRowComponent, {
          location: loc,
          bgColor: locationBgColor,
          expansionKey: getLocationExpansionKey(lobName, queue, loc),
          expandedItems: expandedItems,
          onToggleItem: onToggleItem
        }, loc.id);
      }) : _jsx(SummaryTrainingRows, {
        id: queue.id,
        trainings: queue.trainings,
        bgColor: theme.palette.common.white,
        nestLevel: 3
      })
    })]
  });
};
var LobRowComponent = function (_a) {
  var lob = _a.lob,
    locationBgColor = _a.locationBgColor,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var theme = useTheme();
  var expansionKey = getLobExpansionKey(lob.name);
  var descendantKeys = getLobDescendantExpansionKeys(lob);
  var _b = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey, descendantKeys);
    } : undefined, true),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  var t = useTerminologyTranslation('report', {
    keyPrefix: 'training.summary'
  }).t;
  var displayName = lob.name || t('unassignedLob');
  return _jsxs(_Fragment, {
    children: [_jsx(HierarchyHeaderRow, {
      displayName: displayName,
      backgroundColor: theme.palette.grey[400],
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: 1
    }), expanded && lob.queues.map(function (queue) {
      return _jsx(QueueRowComponent, {
        queue: queue,
        lobName: lob.name,
        locationBgColor: locationBgColor,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem
      }, queue.id);
    })]
  });
};
var RollupLocationRowComponent = function (_a) {
  var loc = _a.loc,
    bgColor = _a.bgColor,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var expansionKey = getOverallLocationExpansionKey(loc);
  var _b = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey);
    } : undefined, true),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  var theme = useTheme();
  return _jsxs(_Fragment, {
    children: [_jsx(HierarchyHeaderRow, {
      displayName: loc.name,
      backgroundColor: bgColor,
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: 1
    }), expanded && _jsx(SummaryTrainingRows, {
      id: loc.id,
      trainings: loc.trainings,
      bgColor: theme.palette.common.white,
      isSummary: true,
      nestLevel: 2
    })]
  });
};
export var SummaryTable = function (_a) {
  var scenarioId = _a.scenarioId;
  var t = useTerminologyTranslation('report', {
    keyPrefix: 'training.summary'
  }).t;
  var _b = useTrainingSummaryTable(scenarioId),
    lobs = _b.lobs,
    summary = _b.summary,
    overallSummary = _b.overallSummary;
  var colors = useScenarioSelected(scenarioId).colors;
  var theme = useTheme();
  var expansionKeys = useMemo(function () {
    var _a;
    return __spreadArray(__spreadArray([], lobs.flatMap(function (lob) {
      return __spreadArray([getLobExpansionKey(lob.name)], lob.queues.flatMap(function (queue) {
        var _a, _b;
        return __spreadArray([getQueueExpansionKey(lob.name, queue)], (_b = (_a = queue.queueLocations) === null || _a === void 0 ? void 0 : _a.map(function (loc) {
          return getLocationExpansionKey(lob.name, queue, loc);
        })) !== null && _b !== void 0 ? _b : [], true);
      }), true);
    }), true), (_a = overallSummary === null || overallSummary === void 0 ? void 0 : overallSummary.map(getOverallLocationExpansionKey)) !== null && _a !== void 0 ? _a : [], true);
  }, [lobs, overallSummary]);
  var _c = useTableRowExpansion(expansionKeys, true),
    expandedItems = _c.expandedItems,
    toggleOne = _c.toggleOne,
    setItemsExpanded = _c.setItemsExpanded;
  var handleToggleItem = function (itemName, descendantKeys) {
    if (descendantKeys === void 0) {
      descendantKeys = [];
    }
    if (!expandedItems[itemName] && descendantKeys.length) {
      setItemsExpanded(descendantKeys, false);
    }
    toggleOne(itemName);
  };
  return _jsx(StyledTableContainer, {
    children: _jsxs(Table, {
      children: [_jsx(TrainingTableHead, {
        queueName: "",
        addColumns: ['Total'],
        bgColor: colors[0],
        disableWeekDate: true
      }), _jsxs(TableBody, {
        children: [lobs.map(function (lob) {
          return _jsx(LobRowComponent, {
            lob: lob,
            locationBgColor: colors[1],
            expandedItems: expandedItems,
            onToggleItem: handleToggleItem
          }, lob.name);
        }), _jsx(HierarchyHeaderRow, {
          displayName: t('allLobs'),
          backgroundColor: colors[0],
          textColor: theme.palette.common.white
        }), (overallSummary === null || overallSummary === void 0 ? void 0 : overallSummary.length) ? overallSummary.map(function (loc) {
          return _jsx(RollupLocationRowComponent, {
            loc: loc,
            bgColor: colors[1],
            expandedItems: expandedItems,
            onToggleItem: handleToggleItem
          }, loc.id);
        }) : _jsx(SummaryTrainingRows, {
          trainings: summary,
          isSummary: true,
          bgColor: colors[1]
        })]
      })]
    })
  });
};